import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLanguage } from "../../context/LanguageContext";
import { Sparkles } from "lucide-react";
import { api } from "../../lib/api";

/**
 * JewishDayBanner — a subtle, elegant strip that appears at the very
 * top of the homepage when "today" carries special Jewish meaning.
 *
 * THE TIME-OF-DAY PROBLEM
 * -----------------------
 * Saturday begins at sundown Friday and ends ~50 minutes after
 * sundown Saturday (havdalah). A banner that just reads the
 * gregorian weekday will keep saying "שבת שלום" all Saturday night,
 * which is wrong — by then it's already מוצאי שבת and we should
 * greet "שבוע טוב".
 *
 * Same problem for chag entry: Wednesday afternoon is "ערב פסח"
 * (greeting "חג שמח") only once candle-lighting is in sight;
 * Wednesday morning the right greeting is just "ערב פסח" (or
 * nothing at all in some cases).
 *
 * Our fix:
 *   • In addition to `/calendar/today`, ask `/calendar/zmanim` for
 *     TODAY *and* YESTERDAY (relative to the user's tz). That gives us
 *     the candle-lighting and havdalah ISO timestamps surrounding the
 *     present moment.
 *   • `buildBanner(now, today, zmaToday, zmaYesterday)` picks the
 *     correct greeting based on whether `now` sits before candles, in
 *     the holy window, or past havdalah.
 *   • A `setInterval` re-runs `buildBanner` every 60 seconds so the
 *     banner naturally transitions to "שבוע טוב" right after havdalah
 *     without a manual page refresh.
 *
 * Localisation note: all greetings flow through i18n via
 * `jewish_day_banner.*` keys. Hebrew copy is byte-identical with the
 * legacy hardcoded literals (he.json holds the exact strings). English
 * UI users see transliterated phrases (Shabbat Shalom, Shavua Tov,
 * etc.) — names of specific chagim that come from HebCal stay in
 * Hebrew either way, wrapped by the English template.
 */
export default function JewishDayBanner() {
  const { t } = useTranslation();
  const { isEn } = useLanguage();
  const [today, setToday] = useState(null);
  const [zmToday, setZmToday] = useState(null);
  const [zmYesterday, setZmYesterday] = useState(null);
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    let alive = true;
    Promise.all([
      api.calendarToday().catch(() => null),
      api.calendarZmanim(isoDate(new Date()), undefined).catch(() => null),
      api.calendarZmanim(isoDate(yesterday(new Date())), undefined).catch(() => null),
    ]).then(([tt, zt, zy]) => {
      if (!alive) return;
      setToday(tt);
      setZmToday(zt);
      setZmYesterday(zy);
    });
    return () => { alive = false; };
  }, []);

  // Tick every minute so the banner crosses havdalah without a reload.
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(id);
  }, []);

  const info = useMemo(
    () => buildBanner(now, today, zmToday, zmYesterday, t),
    [now, today, zmToday, zmYesterday, t],
  );

  if (!info) return null;

  return (
    <div
      data-testid="jewish-day-banner"
      data-tone={info.tone}
      className="w-full bg-gradient-to-l from-transparent via-gold/10 to-transparent border-b border-gold/30"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 py-2.5 flex items-center justify-center gap-3">
        <Sparkles className="h-4 w-4 text-gold shrink-0" strokeWidth={1.5} />
        <p
          className="font-heading text-sm sm:text-base text-foreground text-center"
          dir={isEn ? "ltr" : "rtl"}
        >
          {info.message}
        </p>
        <Sparkles className="h-4 w-4 text-gold shrink-0" strokeWidth={1.5} />
      </div>
    </div>
  );
}

// --- helpers -------------------------------------------------------- //

function isoDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

function yesterday(d) {
  const c = new Date(d);
  c.setDate(c.getDate() - 1);
  return c;
}

/** Parse "HH:MM" into a Date on `baseDate` (local tz). Returns null
 *  on malformed input — callers fall back to weekday-only logic. */
function timeOnDate(baseDate, hhmm) {
  if (!hhmm || typeof hhmm !== "string") return null;
  const m = hhmm.match(/^(\d{1,2}):(\d{2})/);
  if (!m) return null;
  const out = new Date(baseDate);
  out.setHours(parseInt(m[1], 10), parseInt(m[2], 10), 0, 0);
  return out;
}

/**
 * Decide the greeting for `now`. All visible messages route through
 * `t("jewish_day_banner.*")` so Hebrew and English locales render
 * the same conceptual banner with locale-appropriate phrasing.
 */
function buildBanner(now, today, zmToday, zmYesterday, t) {
  if (!now || !today) return null;

  const weekday = now.getDay();              // 0=Sun … 6=Sat
  const events = today.events || [];

  const candlesToday   = timeOnDate(now,            zmToday?.shabbat?.candles_lighting);
  const havdalahToday  = timeOnDate(now,            zmToday?.shabbat?.havdalah);
  const havdalahYday   = timeOnDate(yesterday(now), zmYesterday?.shabbat?.havdalah);
  const candlesYday    = timeOnDate(yesterday(now), zmYesterday?.shabbat?.candles_lighting);

  // 1) On the chag day itself — only while the chag is still "in
  //    effect" (i.e. yesterday's candle-lighting has passed and
  //    havdalah hasn't yet).
  const yomtov = events.find((e) => e.is_yom_tov);
  if (yomtov) {
    const havdalahCutoff = havdalahToday || endOfDay(now);
    if (now < havdalahCutoff) {
      const name = stripErev(yomtov.title);
      return { tone: "chag", message: t("jewish_day_banner.chag", { name }) };
    }
  }

  // 2) Erev chag (day-before that HebCal labels "ערב …").
  const erev = events.find(
    (e) => e.category === "erev" && /^ערב/.test(e.title || ""),
  );
  if (erev) {
    return {
      tone: "erev-chag",
      message: t("jewish_day_banner.erev_chag", { title: erev.title }),
    };
  }

  // 3) Fast day.
  const fast = events.find((e) => e.category === "fast-day");
  if (fast) {
    const isYomKippur = /יום הכפורים|כיפור|Yom Kippur/i.test(fast.title);
    const greeting = isYomKippur
      ? t("jewish_day_banner.fast_yk_greeting")
      : t("jewish_day_banner.fast_minor_greeting");
    return {
      tone: "fast",
      message: t("jewish_day_banner.fast", { title: fast.title, greeting }),
    };
  }

  // 4) Chol HaMoed.
  const cholHamoed = events.find((e) => /חוה״מ|חול המועד/.test(e.title || ""));
  if (cholHamoed) {
    return { tone: "chol-hamoed", message: t("jewish_day_banner.chol_hamoed") };
  }

  // 5) Rosh Chodesh.
  const rosh = events.find((e) => e.category === "rosh-chodesh");
  if (rosh) {
    return {
      tone: "rosh-chodesh",
      message: t("jewish_day_banner.rosh_chodesh", { title: rosh.title }),
    };
  }

  // 6) Saturday — split on havdalah. Before havdalah it's still
  //    Shabbat; after, the right greeting is shavua tov.
  if (weekday === 6) {
    if (havdalahToday && now >= havdalahToday) {
      return { tone: "motzaei-shabbat", message: t("jewish_day_banner.shavua_tov") };
    }
    return { tone: "shabbat", message: t("jewish_day_banner.shabbat_blessed") };
  }

  // 7) Sunday — if Saturday's havdalah was less than ~3 hours ago we
  //    still wish shavua tov. Beyond that, no banner.
  if (weekday === 0) {
    if (havdalahYday) {
      const cutoff = new Date(havdalahYday.getTime() + 3 * 60 * 60 * 1000);
      if (now <= cutoff) {
        return { tone: "motzaei-shabbat", message: t("jewish_day_banner.shavua_tov") };
      }
    }
    return null;
  }

  // 8) Friday — Shabbat Shalom all day, then keep showing it after
  //    candle-lighting since Shabbat has begun.
  if (weekday === 5) {
    if (candlesToday && now >= candlesToday) {
      return { tone: "shabbat", message: t("jewish_day_banner.shabbat_blessed") };
    }
    return { tone: "erev-shabbat", message: t("jewish_day_banner.shabbat_shalom") };
  }

  // 9) Motzaei chag — if yesterday's candle-lighting passed AND today
  //    no `is_yom_tov` AND we're within ~3 hours of yesterday's
  //    havdalah, wish a good week.
  if (candlesYday && havdalahYday && now <= new Date(havdalahYday.getTime() + 3 * 60 * 60 * 1000)) {
    return { tone: "motzaei-chag", message: t("jewish_day_banner.shavua_tov") };
  }

  return null;
}

function endOfDay(d) {
  const c = new Date(d);
  c.setHours(23, 59, 59, 999);
  return c;
}

function stripErev(title) {
  if (!title) return "";
  return title.replace(/^ערב\s+/, "").replace(/^Erev\s+/i, "").trim();
}
